/** @jsxImportSource react */
import { retrieveLocalStorage } from '@common/helpers/localStorage';
import { ConversionSchemaType } from '@common/helpers/transformGwizzyCommunity';
import { gwizzyAsyncHandler } from '@react/gwizzy/asyncHandlers/conversionAsyncHandler';
import GwizzyWizard from '@react/gwizzy/components/GwizzyWizard';
import { getAASchema } from '@react/gwizzy/schema/aaSchema';
import { getConversionSchema } from '@react/gwizzy/schema/conversionSchema';
import { getD2CSchema } from '@react/gwizzy/schema/d2cSchema';
import { getNonD2CSchema } from '@react/gwizzy/schema/nonD2CSchema';
import { getPaidAdSchema } from '@react/gwizzy/schema/paidAdSchema';
import { normalizeResponse } from '@react/services/api';
import { useQuery } from '@react/services/api';
import { useAuth } from '@react/services/auth';
import { useEvents } from '@react/services/events';
import parseName from '@react/shared/helpers/parseName';
import { getClientData } from '@sly/core/constants/localStorage';
import { DEFAULT_EMAIL_VALUE } from '@sly/core/constants/retentionPopup';
import { leadSourceMap } from '@sly/core/constants/uuidActions';
import set from 'lodash/set';
import { useEffect, useMemo, useState } from 'react';

export default function ConversionWizard({
  ctaActions,
  community,
  eventProps = {},
  contactInfo,
  showLocationStep,
  mode,
  formClassName = '',
  isChatMode,
  formWrapperClassName = '',
  enabledDisclosureState = '',
  communitySlugs,
  conversionSchemaOverride = '',
  hasExperimentalOnBack = true,
  submitTextCTA,
}) {
  const { events } = useEvents();
  const createAction = useQuery('createUuidAction');
  const applyFormValidation = useQuery('applyFormValidation');
  const auth = useAuth();
  const { user } = auth;
  const isReadOnlyEmail = !user && retrieveLocalStorage(DEFAULT_EMAIL_VALUE);
  const conversionSchemaType =
    conversionSchemaOverride || community?.conversionSchemaType;
  const [isResidentNameQuestionRequired, setIsResidentNameQuestionRequired] =
    useState(false);

  const isGwizzyQuestionRequiredAPICall = useQuery('isGwizzyQuestionRequired');

  const fetchResidentNameQuestionData = async () => {
    try {
      const response = await isGwizzyQuestionRequiredAPICall({
        type: 'GwizzyQuestion',
        attributes: {
          questionType: 'residentName',
          communitySlug: community.id,
        },
      });
      const normalizedRes = normalizeResponse(response.body);
      if (normalizedRes?.[0]?.info?.isResidentNameRequired) {
        setIsResidentNameQuestionRequired(true);
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchResidentNameQuestionData();
  }, []);

  const conversionSchema = useMemo(() => {
    switch (conversionSchemaType) {
      case ConversionSchemaType.ActiveAdult:
        return getAASchema({
          community,
          contactInfo,
          showLocationStep,
          isReadOnlyEmail,
          isChatMode,
          isResidentNameQuestionRequired,
          submitTextCTA,
        });
      case ConversionSchemaType.D2C:
        return getD2CSchema({
          community,
          contactInfo,
          showLocationStep,
          isReadOnlyEmail,
          isChatMode,
          isResidentNameQuestionRequired,
          submitTextCTA,
        });
      case ConversionSchemaType.NoAgent:
        return getNonD2CSchema({
          community,
          contactInfo,
          showLocationStep,
          isReadOnlyEmail,
          isChatMode,
          isResidentNameQuestionRequired,
          submitTextCTA,
        });
      case ConversionSchemaType.PaidAd:
        return getPaidAdSchema({
          community,
          contactInfo,
          showLocationStep,
          isReadOnlyEmail,
          isChatMode,
          isResidentNameQuestionRequired,
          submitTextCTA,
        });
      default:
        return getConversionSchema({
          community,
          contactInfo,
          showLocationStep,
          isReadOnlyEmail,
          isChatMode,
          isResidentNameQuestionRequired,
          submitTextCTA,
        });
    }
  }, [
    conversionSchemaType,
    community,
    contactInfo,
    showLocationStep,
    isReadOnlyEmail,
    isChatMode,
    isResidentNameQuestionRequired,
    submitTextCTA,
  ]);

  const formInitialValues = useMemo(() => {
    const initialValues = {};
    const emailFromStorage = retrieveLocalStorage(DEFAULT_EMAIL_VALUE);
    const clientData = getClientData();

    if (clientData) {
      set(initialValues, 'email', clientData.email);
      set(initialValues, 'phone', clientData.phone);
      set(initialValues, 'firstName', clientData.firstName);
      set(initialValues, 'lastName', clientData.lastName);
      set(initialValues, 'residentName', clientData.residentName);
      set(initialValues, 'lookingFor', clientData.lookingFor);
      set(initialValues, 'budgetRange', clientData.budgetRange);
      set(initialValues, 'care', clientData.care);
      set(initialValues, 'timing', clientData.moveTimeline);
      set(initialValues, 'city', clientData.city);
      set(initialValues, 'state', clientData.state);
      set(initialValues, 'zip', clientData.zip);
    }

    if (emailFromStorage) {
      initialValues['email'] = emailFromStorage;
    }

    const outOfBudget = retrieveLocalStorage('outOfBudget');

    if (outOfBudget) {
      const outOfBudgetData = JSON.parse(outOfBudget);

      if (outOfBudgetData?.adl) {
        initialValues['adl'] = outOfBudgetData.adl;
      }
    }

    if (!user) {
      return initialValues;
    }

    const { firstName, lastName } = parseName(user?.name);

    set(initialValues, 'email', user.email);
    set(initialValues, 'firstName', firstName);
    set(initialValues, 'lastName', lastName);
    set(initialValues, 'phone', user?.phoneNumber);

    return initialValues;
  }, []);

  return (
    <GwizzyWizard
      initialValues={formInitialValues}
      asyncActionHandlers={gwizzyAsyncHandler({
        events,
        createAction,
        auth,
        community,
        ctaActions,
        applyFormValidation,
        eventProps,
        mode: getClientData()
          ? {
              ...mode,
              leadSource: leadSourceMap.AffiliateLS,
              affiliateSource: 'Homethrive - Self Searcher Browse',
            }
          : mode,
        enabledDisclosureState,
        communitySlugs,
      })}
      // Goes to contact step
      initialStep={getClientData() ? 6 : 0}
      formSchema={conversionSchema}
      isDefaultSubmitDisabled={true}
      localStorageKey={conversionSchemaType}
      hasExperimentalOnBack={hasExperimentalOnBack}
      isChatMode={isChatMode}
      formClassName={formClassName}
      formWrapperClassName={formWrapperClassName}
    />
  );
}
